import _ from 'lodash'
import dayjs from 'dayjs'
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { CreateOrderDto } from '@/core/models/order.model'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { getMeetingLinkByName, selectMeetingLinkByName } from '@/redux/reducers/meeting-link.reducer'
import { createOrder, selectNewlyCreatedOrderId, selectOrderById, selectOrderCreateRequestTracker } from '@/redux/reducers/order.reducer'

import Map from './containers/Map'
import BookingForm, { BookingFormHandle, BookingFormProps, BookingFormValues } from './components/BookingForm'
import BookingConfirmationDialog from './components/BookingConfirmationDialog'

export type BookingPageContextType = {
  address: { description: string; placeId: string } | null
  changeAddress?: (value: BookingPageContextType['address']) => void
}

export const BookingPageContext = createContext<BookingPageContextType>({
  address: null,
  changeAddress: () => {},
})

/**
 * @deprecated not in use
 * @returns
 */
export function BookingPage() {
  const dispatch = useAppDispatch()
  const { meetingLinkName } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })

  const bookingFormRef = useRef<BookingFormHandle>(null)

  const [bookingConfirmationDialogOpen, setBookingConfirmationDialogOpen] = useState(true)
  const [selectedAddress, setSelectedAddress] = useState<BookingPageContextType['address']>(null)

  const meetingLink = useAppSelector((state) => selectMeetingLinkByName(state, meetingLinkName))
  const newlyCreatedOrder = useAppSelector((state) => {
    const orderId = selectNewlyCreatedOrderId(state)
    return orderId ? selectOrderById(state, orderId) : undefined
  })
  const specialities = useMemo(() => _.map(meetingLink?.specialities, 'name'), [meetingLink])

  const schedules = useMemo(() => {
    if (!meetingLink) return []

    const { availabilities, busySlots } = meetingLink

    return _.reduce(
      availabilities,
      (schedules: BookingFormProps['schedules'], availability) => {
        if (availability.isRecurrence) {
          schedules.push({
            address: availability.address,
            busySlots,
            duration: availability.avgMeetingDuration,
            isRecurrence: true,
            meetingPlace: availability.meetingPlace,
            recurrence: _.reduce(
              availability.recurrenceDays,
              (recurrenceResult, recurrenceDay) => {
                if (!recurrenceResult) return []

                const periods = _.map(recurrenceDay.periods, (period) => ({
                  days: [recurrenceDay.day],
                  startTime: period.open,
                  endTime: period.close,
                }))

                if (!recurrenceResult.length) recurrenceResult.push(...periods)

                _.forEach(recurrenceResult, (item) => {
                  for (const period of periods) {
                    if (period.startTime === item.startTime && period.endTime === item.endTime) {
                      item.days = _.union(item.days, period.days)
                    } else {
                      recurrenceResult.push(period)
                    }
                  }
                })

                return recurrenceResult
              },
              [] as BookingFormProps['schedules'][number]['recurrence']
            ),
            specialities: availability.specialities,
          })
        }
        schedules = schedules.concat(
          _.map(availability.scheduledDays, ({ day, periods }) => ({
            address: availability.address,
            busySlots,
            duration: availability.avgMeetingDuration,
            isRecurrence: false,
            meetingPlace: availability.meetingPlace,
            scheduledDay: dayjs(day, 'DD/MM/YYYY').toDate(),
            scheduledPeriods: _.map(periods, (period) => ({
              startTime: period.open,
              endTime: period.close,
            })),
            specialities: availability.specialities,
          }))
        )
        return schedules
      },
      []
    )
  }, [meetingLink])

  const createOrderRequestHandler = useRequestHandler({
    onSuccess: () => {
      setBookingConfirmationDialogOpen(true)
    },
    onError: () => {
      enqueueSnackbar(t('notification.order.create.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectOrderCreateRequestTracker,
  })

  const handleCloseBookingConfirmationDialog = () => {
    setBookingConfirmationDialogOpen(false)
    bookingFormRef.current?.reset()
  }

  const handleSubmit = (values: BookingFormValues) => {
    // let { address, ...otherValues } = values
    // if (!meetingLink || !address) return
    // let createOrderDto: CreateOrderDto = {
    //   ...otherValues,
    //   // meetingLink: meetingLink._id,
    //   // meetingPlace: address,
    // }
    // if (meetingLink.staffs.length === 1 && meetingLink.createdBy === meetingLink.staffs[0]._id) createOrderDto.doctor = meetingLink.createdBy
    // createOrderRequestHandler.dispatch(createOrder(createOrderDto))
  }

  const changeAddress = useCallback((value: BookingPageContextType['address']) => {
    setSelectedAddress(value)
  }, [])

  const contextValue = useMemo(
    () => ({
      address: selectedAddress,
      changeAddress,
    }),
    [changeAddress, selectedAddress]
  )

  useEffect(() => {
    if (!meetingLinkName) return

    dispatch(getMeetingLinkByName(meetingLinkName))
  }, [dispatch, meetingLinkName])

  if (!meetingLink) return null

  return (
    <BookingPageContext.Provider value={contextValue}>
      <Stack direction="row" flex={1}>
        <Stack
          flex={1}
          pb={2}
          sx={{ background: (theme) => theme.palette.background.idle, height: '100%', maxWidth: 560, pl: { xs: '0', sm: '10px' }, zIndex: 1 }}
        >
          {isMobileScreen ? (
            <AppBar position="static">
              <Stack alignItems="center" justifyContent="center" p="30px 16px">
                <Typography variant="h1" sx={{ color: (theme) => theme.palette.common.white, fontSize: '20px', fontWeight: 700 }}>
                  {meetingLink.name}
                </Typography>
              </Stack>
            </AppBar>
          ) : (
            <Typography variant="h1" sx={{ fontSize: '30px', marginTop: '40px', fontWeight: 700, marginBottom: '8px' }}>
              {t('page.booking.title')}
            </Typography>
          )}
          <BookingForm ref={bookingFormRef} onSubmit={handleSubmit} specialities={specialities} schedules={schedules} />
        </Stack>
        {!isMobileScreen && (
          <Box flex={1}>
            <Map />
          </Box>
        )}
        {newlyCreatedOrder && (
          <BookingConfirmationDialog
            onClose={handleCloseBookingConfirmationDialog}
            open={bookingConfirmationDialogOpen}
            orderId={newlyCreatedOrder.referenceId}
          />
        )}
      </Stack>
    </BookingPageContext.Provider>
  )
}

export default BookingPage
